import * as React from 'react';

import {
  Button,
  ContentCard,
  ContentInfo,
  ContentSection,
  ensureValue,
  Menu,
  cn,
  MaybeEmpty,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideCheck,
  LucideChevronDown,
  LucidePencil,
  LucidePlus,
  LucideTrash2,
} from 'lucide-react';

import { ContactRecord } from '../../_graphql/queryAccounts';
import * as accountDetailsState from '../../_state/accountDetails.state';
import * as accountEditingState from '../../_state/accountEditing.state';

import { AddressFormFields } from './AddressFormFields';
import { ContactCard } from './ContactCard';
import { ContactFormDialog } from './ContactFormDialog';

import { keymap } from '~/shared/keymap';

export const AddressDetails = () => {
  const shouldDisableDeletion = useAtomValue(
    accountDetailsState.$shouldDisableDeletion,
  );
  const selectedAddress = useAtomValue(accountDetailsState.$selectedAddress);
  const [{ isPending: isDeletingAddress }, deleteAddress] = useAtom(
    accountEditingState.$deleteRemoteAddress,
  );
  const canEditAccount = useAtomValue(accountEditingState.$canEditAccount);
  const deleteContact = useSetAtom(accountEditingState.$deleteContact);
  const resetContactForm = useSetAtom(accountEditingState.$resetContactForm);
  const updateAccountAddress = useSetAtom(
    accountEditingState.$updateAccountAddress,
  );
  const resetAddressForm = useSetAtom(accountEditingState.$resetAddressForm);
  const canSubmitAddressForm = useAtomValue(
    accountEditingState.$canSubmitAddressForm,
  );

  const [showContactDialog, setShowContactDialog] = React.useState(false);
  const [isEditingAddress, setIsEditingAddress] = React.useState(false);

  const hasContacts = !!selectedAddress?.contacts.length;

  const handleAddContact = () => {
    resetContactForm();
    setShowContactDialog(true);
  };

  const handleDeleteContact = (contact: ContactRecord) => () => {
    deleteContact(contact.id);
  };

  const handleEditContact = (contact: ContactRecord) => () => {
    setShowContactDialog(true);
    resetContactForm(contact);
  };

  const handleDeleteAddress = () => {
    deleteAddress({ id: ensureValue(selectedAddress?.id) });
  };

  const handleStartEditing = () => {
    if (selectedAddress) {
      resetAddressForm(selectedAddress);
      setIsEditingAddress(true);
    }
  };

  const handleCancelEditing = () => {
    setIsEditingAddress(false);
    resetAddressForm();
  };

  const handleSaveEditing = async () => {
    if (selectedAddress) {
      await updateAccountAddress();
      setIsEditingAddress(false);
    }
  };

  return (
    <div className={cn('@AddressDetails', 'flex h-full flex-col gap-4')}>
      <ContactFormDialog
        isOpen={showContactDialog}
        onOpenChange={setShowContactDialog}
      />
      <ContentCard className="shrink-0">
        <ContentSection
          stickyTitle
          titleClassName="py-3"
          title={
            <div className={'flex items-center justify-between'}>
              <div>Address</div>
              {canEditAccount && (
                <div
                  className={cn(
                    'flex items-center gap-6',
                    isEditingAddress && 'pointer-events-none opacity-0',
                  )}
                >
                  <Button
                    variant="link"
                    className="px-0 text-sm font-medium"
                    preventFocus
                    icon={<LucidePencil />}
                    onClick={handleStartEditing}
                  >
                    Edit
                  </Button>
                  <Menu
                    trigger={
                      <Button
                        icon={<LucideTrash2 />}
                        variant="link"
                        color="failure"
                        className="px-0 text-sm font-medium"
                        preventFocus
                        isDisabled={shouldDisableDeletion}
                        isLoading={isDeletingAddress}
                      >
                        Delete <LucideChevronDown size={12} />
                      </Button>
                    }
                    items={[
                      {
                        label: 'Yes, delete this address',
                        onAction: handleDeleteAddress,
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          }
        >
          {isEditingAddress && canEditAccount ? (
            <>
              <AddressFormFields
                className={'grid-cols-4'}
                showNameField={false}
              />
              <div className="col-span-4 mt-3 flex items-end justify-end gap-2 border-t border-neutral/10 pt-5">
                <Button
                  preventFocus
                  variant="outline"
                  color="neutral"
                  className={'border-neutral/20'}
                  onClick={handleCancelEditing}
                >
                  Cancel
                </Button>
                <Button
                  icon={<LucideCheck />}
                  color="primary"
                  variant="outline"
                  preventFocus
                  isLoading={false}
                  isDisabled={!canSubmitAddressForm}
                  onClick={handleSaveEditing}
                  keyboardShortcut={keymap.save}
                >
                  Save
                </Button>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              <ContentInfo
                label="Address type"
                value={selectedAddress?.addressType}
              >
                {(addressType) => addressType}
              </ContentInfo>
              <ContentInfo
                label="Street address"
                value={selectedAddress?.streetAddress}
                className={selectedAddress?.streetAddress2 ? '' : 'col-span-3'}
              >
                {(streetAddress) => streetAddress}
              </ContentInfo>
              {selectedAddress?.streetAddress2 && (
                <ContentInfo
                  label="Address Line 2"
                  value={selectedAddress?.streetAddress2}
                  className="col-span-2"
                >
                  {(streetAddress2) => streetAddress2}
                </ContentInfo>
              )}
              <ContentInfo label="City" value={selectedAddress?.city}>
                {(city) => city}
              </ContentInfo>
              <ContentInfo label="State" value={selectedAddress?.state}>
                {(state) => state}
              </ContentInfo>
              <ContentInfo label="Country" value={selectedAddress?.country}>
                {(country) => country}
              </ContentInfo>
              <ContentInfo
                label="Postal code"
                value={selectedAddress?.postalCode}
              >
                {(postalCode) => postalCode}
              </ContentInfo>

              <ContentInfo
                label="Mailing address"
                value={selectedAddress?.isMailingAddress}
              >
                {(isMailingAddress) => (isMailingAddress ? 'Yes' : 'No')}
              </ContentInfo>
            </div>
          )}
        </ContentSection>
      </ContentCard>
      <ContentCard flexHeight scrollable>
        <ContentSection
          stickyTitle
          titleClassName="py-3"
          className="h-full"
          title={
            <div className={'flex items-center justify-between'}>
              <div>Contacts</div>
              {hasContacts && canEditAccount && (
                <Button
                  icon={<LucidePlus />}
                  variant="link"
                  className={'h-auto p-0 text-sm font-medium'}
                  onClick={handleAddContact}
                  preventFocus
                >
                  Add contact
                </Button>
              )}
            </div>
          }
        >
          <MaybeEmpty
            isEmpty={!hasContacts}
            title="There are no contacts yet"
            description="Are you missing someone?"
            action={
              canEditAccount ? (
                <Button onClick={handleAddContact}>Add contact</Button>
              ) : undefined
            }
          >
            {selectedAddress?.contacts.map((contact) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                onDelete={
                  canEditAccount ? handleDeleteContact(contact) : undefined
                }
                onEdit={canEditAccount ? handleEditContact(contact) : undefined}
              />
            ))}
          </MaybeEmpty>
        </ContentSection>
      </ContentCard>
    </div>
  );
};
